import type {
  CSSProperties,
  ElementType,
  HTMLAttributes,
  ReactNode,
} from 'react';

import { forwardRef } from 'react';

import Box from '~/ui/components/Box/Box';

type ThemeWithSpace = { space: Record<string | number, string> };

type FlexLayoutOwnProps = {
  alignItems?: CSSProperties['alignItems'];
  children?: ReactNode;
  flex?: CSSProperties['flex'];
  flexDirection?: CSSProperties['flexDirection'];
  flexGrow?: CSSProperties['flexGrow'];
  flexShrink?: CSSProperties['flexShrink'];
  flexWrap?: CSSProperties['flexWrap'];
  flexWrapSpace?: number | string;
  inline?: boolean;
  justifyContent?: CSSProperties['justifyContent'];
  space?: number | string;
  sx?: Record<string, unknown>;
};

type ThemeUiSpacingProps = {
  p?: number | string;
  px?: number | string;
  py?: number | string;
  pt?: number | string;
  pb?: number | string;
  pl?: number | string;
  pr?: number | string;
  m?: number | string;
  mx?: number | string;
  my?: number | string;
  mt?: number | string;
  mb?: number | string;
  ml?: number | string;
  mr?: number | string;
  bg?: string;
  width?: string | number;
  height?: string | number;
  maxWidth?: string | number;
};

type FlexLayoutProps = FlexLayoutOwnProps &
  Omit<HTMLAttributes<HTMLDivElement>, keyof FlexLayoutOwnProps> &
  ThemeUiSpacingProps & {
    as?: ElementType;
    disabled?: boolean;
  };

const FlexLayout = forwardRef<HTMLDivElement, FlexLayoutProps>(
  function FlexLayout(
    {
      alignItems,
      children,
      flex,
      flexDirection,
      flexGrow,
      flexShrink,
      flexWrap,
      flexWrapSpace = 0,
      inline = false,
      justifyContent,
      space = 0,
      sx = {},
      ...rest
    },
    ref,
  ) {
    const isFlexWrapped = flexWrap === 'wrap';
    const marginDirection = flexDirection === 'column' ? 'mb' : 'mr';

    return (
      <Box
        mt={isFlexWrapped ? -flexWrapSpace : undefined}
        ref={ref}
        sx={{
          ...sx,
          alignItems,
          display: inline ? 'inline-flex' : 'flex',
          flex,
          flexDirection,
          flexGrow,
          flexShrink,
          flexWrap,
          justifyContent,
          '> *': {
            mt: (theme: ThemeWithSpace) =>
              isFlexWrapped
                ? `${theme.space[flexWrapSpace]} !important`
                : undefined,
          },
          '> :not(:last-child)': {
            [marginDirection]: space,
          },
        }}
        {...rest}
      >
        {children}
      </Box>
    );
  },
);

export default FlexLayout;
